import type {
  AdminAssignmentEntry,
  AdminModelEntry,
  AdminOrganizationEntry,
  AssignmentAccess,
  AssignmentScope,
  Chat,
  ChatAccessLevel,
  ChatContextUsage,
  ChatSummary,
  Folder,
  Group,
  GroupMember,
  McpAuthMode,
  McpServerSummary,
  McpTransport,
  Message,
  ModelId,
  ModelInfo,
  ModelCatalogInput,
  Organization,
  OrganizationMember,
  OrganizationSummary,
  OrgApiKey,
  OrgFeature,
  OrgRole,
  OrgSlackConfigSummary,
  OrgTeamsConfigSummary,
  User,
  UserRole,
} from 'chat-shared'

type McpServerInput = {
  name: string
  url: string
  transport: McpTransport
  authMode: McpAuthMode
  authToken?: string
  enabled: boolean
  access: AssignmentAccess
}

export type SharedUser = {
  id: string
  email: string
  displayName: string
}

export type AdminUserPatch = {
  displayName: string
  email: string
  password: string
  role: UserRole
  isDisabled: boolean
}

export type AdminUserCreate = {
  displayName: string
  email: string
  // Ohne Passwort verschickt der Server eine Willkommens-Mail mit Link zum
  // Setzen des Passworts (erfordert konfiguriertes SMTP, sonst 400).
  password?: string
  role: UserRole
}

export type AdminUserCreated = User & {
  // false z. B. bei SMTP-Fehler — der Account existiert trotzdem.
  welcomeEmailSent: boolean
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    /** Optionale Fehler-Details des Servers (z. B. Ursache bei 502). */
    public readonly detail?: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

const request = async <T>(path: string, init?: RequestInit): Promise<T> => {
  const res = await fetch(`/api${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    ...init,
  })
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }))
    throw new ApiError(res.status, body.error ?? res.statusText, body.detail)
  }
  // 204 No Content: alle Aufrufer mit T=void (logout/delete*) erwarten hier
  // exakt `undefined` zurück, generisch lässt sich das ohne Cast nicht ausdrücken.
  if (res.status === 204) return undefined as T
  return res.json()
}

export const api = {
  login: (email: string, password: string): Promise<User> =>
    request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  logout: (): Promise<void> => request('/auth/logout', { method: 'POST' }),
  me: (): Promise<User> => request('/auth/me'),
  forgotPassword: (email: string): Promise<void> =>
    request('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),
  resetPassword: (token: string, password: string): Promise<void> =>
    request('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    }),

  models: (): Promise<Array<ModelInfo>> => request('/models'),

  listChats: (): Promise<Array<ChatSummary>> => request('/chats'),
  createChat: (model: string): Promise<Chat> =>
    request('/chats', { method: 'POST', body: JSON.stringify({ model }) }),
  getChat: (
    id: string,
  ): Promise<
    Chat & {
      messages: Array<Message>
      access: ChatAccessLevel
      contextUsage: ChatContextUsage
    }
  > => request(`/chats/${id}`),
  // Kontext komprimieren (wie „Compact" bei claude.ai) — fasst die History
  // per LLM zusammen; Folge-Anfragen starten ab der Zusammenfassung.
  compactChat: (
    id: string,
    model?: string,
  ): Promise<{ summaryMessage: Message; contextUsage: ChatContextUsage }> =>
    request(`/chats/${id}/compact`, {
      method: 'POST',
      body: JSON.stringify({ model }),
    }),
  renameChat: (id: string, title: string): Promise<void> =>
    request(`/chats/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ title }),
    }),
  deleteChat: (id: string): Promise<void> =>
    request(`/chats/${id}`, { method: 'DELETE' }),
  listChatShares: (id: string): Promise<Array<SharedUser>> =>
    request(`/chats/${id}/shares`),
  shareChat: (id: string, email: string): Promise<SharedUser> =>
    request(`/chats/${id}/shares`, {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),
  unshareChat: (id: string, userId: string): Promise<void> =>
    request(`/chats/${id}/shares/${userId}`, { method: 'DELETE' }),
  setChatFolder: (id: string, folderId: string | null): Promise<void> =>
    request(`/chats/${id}/folder`, {
      method: 'PATCH',
      body: JSON.stringify({ folderId }),
    }),
  assignChat: (id: string, assignment: AssignmentScope): Promise<void> =>
    request(`/chats/${id}/assignment`, {
      method: 'PUT',
      body: JSON.stringify(assignment),
    }),
  unassignChat: (id: string): Promise<void> =>
    request(`/chats/${id}/assignment`, { method: 'DELETE' }),

  listFolders: (): Promise<Array<Folder>> => request('/folders'),
  createFolder: (name: string): Promise<Folder> =>
    request('/folders', { method: 'POST', body: JSON.stringify({ name }) }),
  renameFolder: (id: string, name: string): Promise<void> =>
    request(`/folders/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ name }),
    }),
  assignFolder: (id: string, assignment: AssignmentScope): Promise<void> =>
    request(`/folders/${id}/assignment`, {
      method: 'PUT',
      body: JSON.stringify(assignment),
    }),
  unassignFolder: (id: string): Promise<void> =>
    request(`/folders/${id}/assignment`, { method: 'DELETE' }),
  deleteFolder: (id: string): Promise<void> =>
    request(`/folders/${id}`, { method: 'DELETE' }),

  listOrganizations: (): Promise<Array<OrganizationSummary>> =>
    request('/organizations'),
  renameOrganization: (id: string, name: string): Promise<void> =>
    request(`/organizations/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ name }),
    }),
  // Systemadmin-only, siehe apps/chat-api/src/http/admin.ts.
  deleteOrganizationAsAdmin: (id: string): Promise<void> =>
    request(`/admin/organizations/${id}`, { method: 'DELETE' }),
  // Feature-Freischaltung pro Organisation (Systemadmin-only) — vollständige
  // Ersetzung der Liste.
  updateAdminOrgFeatures: (
    orgId: string,
    features: Array<OrgFeature>,
  ): Promise<void> =>
    request(`/admin/organizations/${orgId}/features`, {
      method: 'PUT',
      body: JSON.stringify({ features }),
    }),
  listOrgMembers: (orgId: string): Promise<Array<OrganizationMember>> =>
    request(`/organizations/${orgId}/members`),
  inviteOrgMember: (
    orgId: string,
    email: string,
    role: OrgRole,
  ): Promise<OrganizationMember> =>
    request(`/organizations/${orgId}/members`, {
      method: 'POST',
      body: JSON.stringify({ email, role }),
    }),
  updateOrgMemberRole: (
    orgId: string,
    userId: string,
    role: OrgRole,
  ): Promise<void> =>
    request(`/organizations/${orgId}/members/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify({ role }),
    }),
  removeOrgMember: (orgId: string, userId: string): Promise<void> =>
    request(`/organizations/${orgId}/members/${userId}`, { method: 'DELETE' }),

  listGroups: (orgId: string): Promise<Array<Group>> =>
    request(`/organizations/${orgId}/groups`),
  createGroup: (orgId: string, name: string): Promise<Group> =>
    request(`/organizations/${orgId}/groups`, {
      method: 'POST',
      body: JSON.stringify({ name }),
    }),
  deleteGroup: (orgId: string, groupId: string): Promise<void> =>
    request(`/organizations/${orgId}/groups/${groupId}`, { method: 'DELETE' }),
  listGroupMembers: (
    orgId: string,
    groupId: string,
  ): Promise<Array<GroupMember>> =>
    request(`/organizations/${orgId}/groups/${groupId}/members`),
  addGroupMember: (
    orgId: string,
    groupId: string,
    email: string,
  ): Promise<GroupMember> =>
    request(`/organizations/${orgId}/groups/${groupId}/members`, {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),
  removeGroupMember: (
    orgId: string,
    groupId: string,
    userId: string,
  ): Promise<void> =>
    request(`/organizations/${orgId}/groups/${groupId}/members/${userId}`, {
      method: 'DELETE',
    }),

  listAdminUsers: (): Promise<Array<User>> => request('/admin/users'),
  createAdminUser: (input: AdminUserCreate): Promise<AdminUserCreated> =>
    request('/admin/users', { method: 'POST', body: JSON.stringify(input) }),
  updateAdminUser: (
    userId: string,
    patch: Partial<AdminUserPatch>,
  ): Promise<void> =>
    request(`/admin/users/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
  listAdminAssignments: (): Promise<Array<AdminAssignmentEntry>> =>
    request('/admin/assignments'),
  listAdminOrganizations: (): Promise<Array<AdminOrganizationEntry>> =>
    request('/admin/organizations'),
  createOrganizationAsAdmin: (
    name: string,
    ownerEmail?: string,
  ): Promise<Organization> =>
    request('/admin/organizations', {
      method: 'POST',
      body: JSON.stringify({ name, ownerEmail }),
    }),
  listAdminModels: (): Promise<Array<AdminModelEntry>> =>
    request('/admin/models'),
  // PUT mit modelId im Body statt Pfad — die Modell-IDs enthalten `/` und `:`.
  // Legt neue Modelle an bzw. ersetzt bestehende vollständig.
  upsertAdminModel: (
    modelId: string,
    input: ModelCatalogInput,
  ): Promise<AdminModelEntry> =>
    request('/admin/models', {
      method: 'PUT',
      body: JSON.stringify({ modelId, ...input }),
    }),
  deleteAdminModel: (modelId: string): Promise<void> =>
    request(`/admin/models?modelId=${encodeURIComponent(modelId)}`, {
      method: 'DELETE',
    }),

  // MCP-Server werden ausschließlich im Kontext einer Organisation verwaltet
  // (siehe apps/chat-api/src/http/orgMcpServers.ts).
  listOrgMcpServers: (orgId: string): Promise<Array<McpServerSummary>> =>
    request(`/organizations/${orgId}/mcp-servers`),
  createOrgMcpServer: (
    orgId: string,
    input: McpServerInput,
  ): Promise<McpServerSummary> =>
    request(`/organizations/${orgId}/mcp-servers`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  updateOrgMcpServer: (
    orgId: string,
    serverId: string,
    patch: Partial<McpServerInput>,
  ): Promise<McpServerSummary> =>
    request(`/organizations/${orgId}/mcp-servers/${serverId}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
  deleteOrgMcpServer: (orgId: string, serverId: string): Promise<void> =>
    request(`/organizations/${orgId}/mcp-servers/${serverId}`, {
      method: 'DELETE',
    }),
  testOrgMcpServer: (
    orgId: string,
    serverId: string,
  ): Promise<{ tools: Array<{ name: string; description?: string }> }> =>
    request(`/organizations/${orgId}/mcp-servers/${serverId}/test`, {
      method: 'POST',
    }),
  startOrgMcpOAuth: (
    orgId: string,
    serverId: string,
  ): Promise<{ authorizationUrl: string | null }> =>
    request(`/organizations/${orgId}/mcp-servers/${serverId}/oauth/start`, {
      method: 'POST',
    }),

  // Organisations-API-Schlüssel für Drittsysteme (siehe
  // apps/chat-api/src/http/orgApiKeys.ts). Der Klartext-Schlüssel steht nur
  // in der Antwort von createOrgApiKey (show-once).
  listOrgApiKeys: (orgId: string): Promise<Array<OrgApiKey>> =>
    request(`/organizations/${orgId}/api-keys`),
  createOrgApiKey: (
    orgId: string,
    name: string,
  ): Promise<{ apiKey: OrgApiKey; key: string }> =>
    request(`/organizations/${orgId}/api-keys`, {
      method: 'POST',
      body: JSON.stringify({ name }),
    }),
  revokeOrgApiKey: (orgId: string, keyId: string): Promise<void> =>
    request(`/organizations/${orgId}/api-keys/${keyId}`, { method: 'DELETE' }),

  // Microsoft-Teams-Anbindung der Organisation (siehe
  // apps/chat-api/src/http/orgTeamsConfig.ts). 404 ⇒ noch nicht konfiguriert.
  getOrgTeamsConfig: async (
    orgId: string,
  ): Promise<OrgTeamsConfigSummary | undefined> => {
    try {
      return await request(`/organizations/${orgId}/teams-config`)
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return undefined
      throw err
    }
  },
  // Startet den Entra-Admin-Consent-Flow: liefert die Microsoft-URL, zu der
  // der Browser weiterleitet (oder die an den IT-Admin der Org geht).
  startOrgTeamsConsent: (orgId: string): Promise<{ url: string }> =>
    request(`/organizations/${orgId}/teams-config/consent`, { method: 'POST' }),
  // entraTenantId nur für Systemadmins (manuelles Onboarding); regulär
  // entsteht die Tenant-Bindung über den Consent-Flow.
  saveOrgTeamsConfig: (
    orgId: string,
    input: {
      enabled: boolean
      model?: ModelId
      entraTenantId?: string
    },
  ): Promise<OrgTeamsConfigSummary> =>
    request(`/organizations/${orgId}/teams-config`, {
      method: 'PUT',
      body: JSON.stringify(input),
    }),
  deleteOrgTeamsConfig: (orgId: string): Promise<void> =>
    request(`/organizations/${orgId}/teams-config`, { method: 'DELETE' }),

  // Slack-Anbindung der Organisation (siehe
  // apps/chat-api/src/http/orgSlackConfig.ts). 404 ⇒ noch nicht konfiguriert.
  getOrgSlackConfig: async (
    orgId: string,
  ): Promise<OrgSlackConfigSummary | undefined> => {
    try {
      return await request(`/organizations/${orgId}/slack-config`)
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return undefined
      throw err
    }
  },
  saveOrgSlackConfig: (
    orgId: string,
    input: {
      enabled: boolean
      botToken?: string
      signingSecret?: string
      model?: ModelId
    },
  ): Promise<OrgSlackConfigSummary> =>
    request(`/organizations/${orgId}/slack-config`, {
      method: 'PUT',
      body: JSON.stringify(input),
    }),
  deleteOrgSlackConfig: (orgId: string): Promise<void> =>
    request(`/organizations/${orgId}/slack-config`, { method: 'DELETE' }),
}
